import type { PlatformEnum, WishlistCreator } from '@fluenceur/types';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { isIdentityLocked } from '@/components/plan-locks/creator-locks';
import { useInvitationData } from '@/contexts/invitation-context';
import {
  contactResolveQueryKey,
  fetchContactResolution,
} from '@/hooks/queries/use-resolve-contact-query';

/**
 * Per-line delivery channel:
 * - `resolved`: identity known — sent by identity, personal link delivery.
 * - `emailOnly`: no identity resolved but an email is on file — the backend
 *   provisions the identity from the email at proposal time.
 * - `resolving`: identity resolution still in flight (bulk only).
 * - `unreachable`: no identity and no email — the line cannot be sent and is
 *   surfaced as a failed result instead of blocking the batch.
 */
export type CollabRecipientChannel = 'resolved' | 'emailOnly' | 'resolving' | 'unreachable';

export interface CollabWizardRecipient {
  /** Stable list key (identity id, wishlist row id, or 'mono'). */
  key: string;
  displayName: string;
  avatarUrl: string | null;
  email: string | null;
  creatorIdentityId: string | null;
  channel: CollabRecipientChannel;
  /** The brand's plan masks this creator's address. */
  emailLocked?: boolean;
}

const wishlistDisplayName = (item: WishlistCreator): string =>
  item.fullName?.trim() || `@${item.username}`;

/**
 * Derives the wizard's recipient lines from the invitation target. Mono
 * (contact-card entry) reads the identity seeded into the store; bulk resolves
 * every wishlist line in parallel through the shared contact-resolve cache
 * (lazy provisioning included). No line ever blocks the flow: an unresolvable
 * line without email is carried as `unreachable` and reported per line.
 */
export const useCollabRecipients = (): CollabWizardRecipient[] => {
  const {
    target,
    creator,
    recipientEmail,
    collabRecipientIdentityId,
    collabReseedRecipient,
    collabRecipientDisplay,
  } = useInvitationData();

  const wishlistItems = useMemo(
    () => (target?.type === 'wishlist-bulk' ? target.data : []),
    [target]
  );

  const resolutions = useQueries({
    queries: wishlistItems.map((item) => ({
      queryKey: contactResolveQueryKey('creator', item.username, item.platform as PlatformEnum),
      queryFn: () =>
        fetchContactResolution({
          entryType: 'creator',
          id: item.username,
          platform: item.platform as PlatformEnum,
          sourceCreatorId: item.sourceCreatorId ?? undefined,
        }),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  return useMemo(() => {
    if (target?.type === 'wishlist-bulk') {
      return wishlistItems.map((item, index) => {
        const resolution = resolutions[index];
        const creatorIdentityId = resolution?.data?.creatorIdentityId ?? null;
        const email = item.email ?? resolution?.data?.primaryEmail ?? null;
        const channel: CollabRecipientChannel = creatorIdentityId
          ? 'resolved'
          : resolution?.isPending
            ? 'resolving'
            : email
              ? 'emailOnly'
              : 'unreachable';
        return {
          key: item.id,
          displayName: wishlistDisplayName(item),
          avatarUrl: item.profilePictureUrl,
          email,
          creatorIdentityId,
          channel,
          emailLocked: isIdentityLocked(item),
        };
      });
    }

    if (creator) {
      const channel: CollabRecipientChannel = collabRecipientIdentityId
        ? 'resolved'
        : recipientEmail
          ? 'emailOnly'
          : 'unreachable';
      return [
        {
          key: collabRecipientIdentityId ?? 'mono',
          displayName: creator.name ?? '',
          avatarUrl: creator.profilePictureUrl ?? null,
          email: recipientEmail,
          creatorIdentityId: collabRecipientIdentityId,
          channel,
        },
      ];
    }

    // Reseed entry (drawer): the mono line is built from the seeded recipient
    // fields, so no full Creator object is needed. The identity is always known
    // (the previous collab had a recipient), so the channel is 'resolved'.
    // Checked before the generic mono branch: a reseed also carries an identity
    // and email but must keep its own display fields.
    if (collabReseedRecipient && collabRecipientIdentityId) {
      return [
        {
          key: collabRecipientIdentityId,
          displayName: collabReseedRecipient.displayName,
          avatarUrl: collabReseedRecipient.avatarUrl,
          email: collabReseedRecipient.email,
          creatorIdentityId: collabRecipientIdentityId,
          channel: 'resolved',
        },
      ];
    }

    // Mono propose entry for a contact with an identity but no social profile
    // (never linked to Instagram/TikTok): no Creator object exists, so build the
    // line from the seeded identity + email. Display name falls back to the email
    // when the resolved identity carried none; the avatar defaults to initials.
    if (collabRecipientIdentityId || recipientEmail) {
      return [
        {
          key: collabRecipientIdentityId ?? 'mono',
          displayName: collabRecipientDisplay?.displayName ?? recipientEmail ?? '',
          avatarUrl: collabRecipientDisplay?.avatarUrl ?? null,
          email: recipientEmail,
          creatorIdentityId: collabRecipientIdentityId,
          channel: collabRecipientIdentityId ? 'resolved' : 'emailOnly',
        },
      ];
    }

    return [];
  }, [
    target,
    wishlistItems,
    resolutions,
    creator,
    recipientEmail,
    collabRecipientIdentityId,
    collabReseedRecipient,
    collabRecipientDisplay,
  ]);
};
